import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { MediaTypes } from '../../commands/practitioner/enums/MediaTypes';
import ReactPlayer from 'react-player/lazy';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import { ReactPlayerProps } from 'react-player';
import useClientMediaState from '../../hooks/useClientMediaState/useClientMediaState';
import { PLayerTriggeredBy } from '../../commands/practitioner/enums/PLayerTriggeredBy';

interface DialogMediaPlayerProps {
  ReactPlayerProps: ReactPlayerProps;
  ReactPlayerTriggeredBy: PLayerTriggeredBy;
  handleMediaEnd?: any;
  seekToMs?: number;
  seekEventId?: number;
}

function DialogMediaPlayer(props: DialogMediaPlayerProps) {
  const { ReactPlayerProps, handleMediaEnd, seekToMs, seekEventId } = props;
  const { practitionerMediaType, practitionerMediaURI } = usePractitionerControlsContext();
  const { clientMediaURI, clientMediaType } = useClientMediaState();

  const playerRef = useRef(null);
  const lastHandledSeekEventId = useRef<number | null>(null);

  useEffect(() => {
    if (!playerRef.current) {
      return;
    }
    // @ts-ignore
    playerRef.current.seekTo(0);
  }, [clientMediaURI]);

  useEffect(() => {
    if (props.ReactPlayerTriggeredBy !== PLayerTriggeredBy.Client) {
      return;
    }
    if (!playerRef.current || typeof seekToMs !== 'number' || !Number.isFinite(seekToMs)) {
      return;
    }
    if (!seekEventId || lastHandledSeekEventId.current === seekEventId) {
      return;
    }

    playerRef.current.seekTo(seekToMs / 1000, 'seconds');
    lastHandledSeekEventId.current = seekEventId;
  }, [props.ReactPlayerTriggeredBy, seekToMs, seekEventId]);

  const hasEndedRef = useRef(false);
  const handleMediaEndRef = useRef(handleMediaEnd);
  handleMediaEndRef.current = handleMediaEnd;

  const onEnded = useCallback(() => {
    hasEndedRef.current = true;
    handleMediaEndRef.current?.();
  }, []);

  // When playback resumes after the video ended, seek to 0 so it replays
  // from the beginning. Done here (not inside onEnded) to avoid disrupting
  // YouTube's internal state machine during the ended callback.
  const prevPlayingRef = useRef(false);
  useEffect(() => {
    const playing = !!ReactPlayerProps.playing;
    const wasPlaying = prevPlayingRef.current;
    prevPlayingRef.current = playing;

    if (playing && !wasPlaying && hasEndedRef.current) {
      // @ts-ignore
      if (playerRef.current) playerRef.current.seekTo(0);
      hasEndedRef.current = false;
    }
  }, [ReactPlayerProps.playing]);

  const RenderType = useMemo(() => {
    switch (props.ReactPlayerTriggeredBy === PLayerTriggeredBy.Practitioner ? practitionerMediaType : clientMediaType) {
      case MediaTypes.VIDEO:
        return (
          <ReactPlayer
            className="react-player"
            width="100%"
            height="100%"
            ref={playerRef}
            {...ReactPlayerProps}
            onEnded={onEnded}
            config={{
              youtube: {
                playerVars: { showinfo: 1, disableAds: true },
              },
            }}
          />
        );
      case MediaTypes.AUDIO:
        return <ReactPlayer ref={playerRef} style={{ display: 'none' }} {...ReactPlayerProps} onEnded={onEnded} />;
      case MediaTypes.IMAGE:
        return (
          <img
            src={props.ReactPlayerTriggeredBy === PLayerTriggeredBy.Client ? clientMediaURI : practitionerMediaURI}
            style={{ aspectRatio: 'auto' }}
          />
        );
      default:
        return <ReactPlayer />;
    }
  }, [practitionerMediaType, clientMediaURI, ReactPlayerProps, practitionerMediaURI, clientMediaType, onEnded]);

  return <>{RenderType}</>;
}

export default DialogMediaPlayer;
